import { CheckCircle, Circle, Target, Ruler, MagnifyingGlass, Wrench, ShieldCheck } from "@phosphor-icons/react";

const TextBlock = ({ n, icon: Icon, title, value }) => (
  <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
    <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-3 bg-slate-50">
      <span className="w-7 h-7 rounded-sm bg-blue-700 text-white font-mono text-xs font-bold flex items-center justify-center">{n}</span>
      <Icon size={18} weight="duotone" className="text-blue-700" />
      <h3 className="text-sm font-semibold tracking-wide uppercase text-slate-700">{title}</h3>
    </div>
    <div className="p-5"><p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{value || <span className="text-slate-300">—</span>}</p></div>
  </div>
);

// Read-only blueprint (used for shared links). Interactive tracking lives in DmaicTracking.
export default function DmaicBlueprint({ doc, onToggle }) {
  const p = doc.payload || {};
  const tasks = p.improveTasks || [];
  const progress = doc.progress || { done: 0, total: 0, pct: 0 };

  return (
    <div className="space-y-3" data-testid="dmaic-blueprint">
      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold tracking-[0.08em] uppercase text-slate-500">Improvement Progress</span>
          <span className="font-mono text-sm font-semibold text-slate-700" data-testid="progress-percent">{progress.done}/{progress.total} · {progress.pct}%</span>
        </div>
        <div className="h-3 bg-slate-100 rounded-sm overflow-hidden">
          <div className={`h-full ${progress.pct === 100 ? "bg-emerald-500" : "bg-blue-600"}`} style={{ width: `${progress.pct}%` }} />
        </div>
      </div>

      <TextBlock n="D" icon={Target} title="Define" value={p.define} />
      <TextBlock n="M" icon={Ruler} title="Measure" value={p.measure} />
      <TextBlock n="A" icon={MagnifyingGlass} title="Analyse" value={p.analyse} />

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-3 bg-slate-50">
          <span className="w-7 h-7 rounded-sm bg-blue-700 text-white font-mono text-xs font-bold flex items-center justify-center">I</span>
          <Wrench size={18} weight="duotone" className="text-blue-700" />
          <h3 className="text-sm font-semibold tracking-wide uppercase text-slate-700">Improve — Action Checklist</h3>
        </div>
        <div className="divide-y divide-slate-100">
          {tasks.length === 0 && <p className="px-5 py-3 text-sm text-slate-400">No improvement actions defined.</p>}
          {tasks.map((t) => {
            const Row = onToggle ? "button" : "div";
            return (
              <Row
                key={t.id}
                data-testid={`dmaic-task-${t.id}`}
                {...(onToggle ? { onClick: () => onToggle(t.id, t.status) } : {})}
                className={`w-full flex items-center gap-3 px-5 py-3 text-left ${onToggle ? "hover:bg-slate-50 transition-colors" : ""}`}
              >
                {t.status ? <CheckCircle size={22} weight="fill" className="text-emerald-500 shrink-0" /> : <Circle size={22} className="text-slate-300 shrink-0" />}
                <span className={`text-sm ${t.status ? "text-slate-400 line-through" : "text-slate-800"}`}>{t.name || "Untitled action"}</span>
              </Row>
            );
          })}
        </div>
      </div>

      <TextBlock n="C" icon={ShieldCheck} title="Control" value={p.control} />
    </div>
  );
}
